import React, { useContext, useEffect, useState, useRef } from 'react';
import DataContext from '../../stores/DataContextProvider';
import { useNavigate } from 'react-router-dom';
import backendIP from '../../utils/serverData';
import Popup from '../popup/Popup';
import './TestScreen11.css';
import Goal from '../controllers/Goal';

const delay = (ms, signal) => {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new Error('Aborted'));
    const t = setTimeout(() => resolve(), ms);
    signal.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new Error('Aborted'));
    });
  });
};

const TestScreen12 = () => {
  const { g, sk, instruction, selectedOptions, folderPath } = useContext(DataContext);
  const [displayScript, setDisplayScript] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupError, setPopupError] = useState(null);
  const audioRef = useRef(new Audio());
  const abortControllerRef = useRef(new AbortController());
  const navigate = useNavigate();

  const [isTestRunning, setIsTestRunning] = useState(false);
  const [numberOfStimuli, setNumberOfStimuli] = useState(0);
  const [targetStimuli, setTargetStimuli] = useState('');
  const [targetPercentage, setTargetPercentage] = useState(0);
  const [duration, setDuration] = useState(''); // seconds

  const [targetScore, setTargetScore] = useState(0);
  const [nonTargetScore, setNonTargetScore] = useState(0);
  const [targetStimulusCount, setTargetStimulusCount] = useState(0);
  const [nonTargetStimulusCount, setNonTargetStimulusCount] = useState(0);
const [isWithinResponseWindow, setIsWithinResponseWindow] = useState(false);
  const [responseWindow, setResponseWindow] = useState(); // seconds
  const [currentStimulusIndex, setCurrentStimulusIndex] = useState(0);
  const timeoutRef = useRef(null);
  const respTimeoutRef = useRef(null); // response-window timer
  const testTimeoutRef = useRef(null);
  
  const stimulusSequenceRef = useRef([]);
  const isPlayingRef = useRef(false);
  const finalGapSeconds = 10; // seconds

  // Helpers
  const getOption = (keys = []) => {
    if (!selectedOptions) return undefined;
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(selectedOptions, k)) return selectedOptions[k];
    }
    return undefined;
  };

  const parseDurationMinutes = (durationOption) => {
    if (durationOption == null) return 5;
    if (typeof durationOption === 'object') {
      if (typeof durationOption.value === 'number') return durationOption.value;
      if (typeof durationOption.value === 'string') {
        const m = durationOption.value.match(/\d+/);
        if (m) return parseInt(m[0], 10);
      }
      if (typeof durationOption.label === 'string') {
        const m = durationOption.label.match(/\d+/);
        if (m) return parseInt(m[0], 10);
      }
      for (const v of Object.values(durationOption)) {
        if (typeof v === 'string') {
          const m = v.match(/\d+/);
          if (m) return parseInt(m[0], 10);
        }
        if (typeof v === 'number') return v;
      }
    }
    if (typeof durationOption === 'string') {
      const m = durationOption.match(/\d+/);
      if (m) return parseInt(m[0], 10);
    }
    if (typeof durationOption === 'number') return durationOption;
    return 5;
  };

  const normalizeRawPath = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const forward = raw.replace(/\\/g, '/').trim();
    const leadingUNC = forward.startsWith('//') ? '//' : '';
    let tmp = forward.replace(/^\/+/, '').replace(/\/+/g, '/');
    if (leadingUNC) tmp = leadingUNC + tmp;
    const parts = tmp.split('/').filter(Boolean);
    const dedupedParts = parts.reduce((acc, p) => { if (acc.length === 0 || acc[acc.length - 1] !== p) acc.push(p); return acc; }, []);
    const deduped = (leadingUNC ? '//' : '') + dedupedParts.join('/');
    return { original: raw, forward, deduped, parts: dedupedParts, leadingUNC };
  };

  const buildCandidates = (rawPath) => {
    const res = normalizeRawPath(rawPath);
    if (!res) return [];
    const out = [res.original, res.forward, res.deduped, res.deduped.replace(/^\/+/, ''), ((res.leadingUNC ? '\\\\' : '') + res.parts.join('\\'))];
    const lowerParts = res.parts.map(p => p.toLowerCase());
    const baseIndex = lowerParts.findIndex(p => /cog/i.test(p) || /cogniselsusatt/i.test(p) || /cogwordssus/i.test(p));
    if (baseIndex >= 0) {
      const base = res.parts.slice(0, baseIndex + 1);
      const last = res.parts[res.parts.length - 1];
      out.push((res.leadingUNC ? '//' : '') + [...base, last].join('/'));
    }
    if (res.parts.length >= 2) out.push(res.parts.slice(-2).join('/'));
    out.push(res.parts.slice(-1).join('/'));
    return Array.from(new Set(out.filter(Boolean)));
  };

  const tryListFilesWithCandidates = async (candidates) => {
    for (const cand of candidates) {
      try {
        const resp = await fetch(`${backendIP}/audio/listfiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderPath: cand }),
          signal: abortControllerRef.current.signal,
        });
        if (!resp.ok) continue;
        const json = await resp.json();
        return { files: json, usedPath: cand };
      } catch (e) {
        if (abortControllerRef.current.signal.aborted) break;
      }
    }
    return null;
  };

  // Parse selectedOptions -> start test
  useEffect(() => {
    if (!selectedOptions || Object.keys(selectedOptions).length === 0) return;

    const numStimOpt = getOption(['Number of Stimuli', 'NumberOfStimuli', 'Number']);
    const numStimuliRaw = numStimOpt?.label ?? numStimOpt?.value ?? numStimOpt;
    const numStimuli = parseInt(numStimuliRaw, 10) || 0;

    const targetStimuliOpt = getOption(['Target Stimuli', 'TargetStimuli', 'Target']);
    const targetStimuliLabel = (targetStimuliOpt?.label ?? targetStimuliOpt?.value ?? targetStimuliOpt ?? '').toString();

    const targetPercentageOpt = getOption(['Target Percentage', 'TargetPercentage', 'Target %']);
    const targetPercentageValue = targetPercentageOpt != null ? parseInt(targetPercentageOpt, 10) : 0;

    const durationOption = getOption(['Duration', 'Time Duration', 'TimeDuration', 'Time']);
    const durationMinutes = parseDurationMinutes(durationOption);
    const durationSeconds = durationMinutes * 60;

    const responseWindowOpt = getOption(['Response Window', 'ResponseWindow', 'Response']);
    let responseWindowSeconds = 3;
    if (responseWindowOpt != null) {
      if (typeof responseWindowOpt === 'number') responseWindowSeconds = responseWindowOpt > 1000 ? responseWindowOpt / 1000 : responseWindowOpt;
      else if (typeof responseWindowOpt === 'string') {
        const m = responseWindowOpt.match(/\d+/);
        if (m) {
          const val = parseInt(m[0], 10);
          responseWindowSeconds = val > 1000 ? val / 1000 : val;
        }
      } else if (typeof responseWindowOpt === 'object') {
        const raw = responseWindowOpt.value ?? responseWindowOpt.label ?? responseWindowOpt;
        if (typeof raw === 'number') responseWindowSeconds = raw > 1000 ? raw / 1000 : raw;
        else if (typeof raw === 'string') {
          const m = raw.match(/\d+/);
          if (m) responseWindowSeconds = parseInt(m[0], 10) > 1000 ? parseInt(m[0], 10) / 1000 : parseInt(m[0], 10);
        }
      }
    }

    if (!isNaN(numStimuli) && numStimuli > 0) {
      // reset
      stimulusSequenceRef.current = [];
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
      if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
      try { abortControllerRef.current.abort(); } catch (e) {}
      abortControllerRef.current = new AbortController();

      setNumberOfStimuli(numStimuli);
      setTargetStimuli(targetStimuliLabel);
      setTargetPercentage(targetPercentageValue);
      setDuration(durationSeconds);
      setResponseWindow(responseWindowSeconds);

      setIsTestRunning(true);
      setPopupError(null);
    } else {
      console.error('Invalid Number of Stimuli:', numStimuli);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  useEffect(() => {
    if (folderPath && numberOfStimuli > 0 && duration > 0 && isTestRunning) {
      fetchSelectedAudio();
    }
    return () => {
      try { abortControllerRef.current.abort(); } catch (e) {}
      try { audioRef.current.pause(); } catch (e) {}
      audioRef.current.src = '';
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
      if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath, numberOfStimuli, duration, isTestRunning]);

  const fetchAudioBlobAndDuration = async (filenameWithPath) => {
    const resp = await fetch(`${backendIP}/audio/getaudio`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filenameWithPath }),
      signal: abortControllerRef.current.signal,
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(()=>'');
      throw new Error(`getaudio failed ${resp.status} ${txt}`);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const temp = new Audio();
    temp.src = url;
    await new Promise((resolve, reject) => {
      const onLoaded = () => { cleanup(); resolve(); };
      const onErr = () => { cleanup(); reject(new Error('loadedmetadata failed')); };
      const cleanup = () => { temp.removeEventListener('loadedmetadata', onLoaded); temp.removeEventListener('error', onErr); };
      temp.addEventListener('loadedmetadata', onLoaded);
      temp.addEventListener('error', onErr);
    });
    const dur = temp.duration || 0;
    return { url, duration: dur };
  };

  const ensureCombinedLength = (combinedFiles, numberDesired, nonTargetFiles, targetFile) => {
    const arr = combinedFiles.slice();
    while (arr.length < numberDesired) {
      if (nonTargetFiles && nonTargetFiles.length > 0) {
        arr.push(nonTargetFiles[Math.floor(Math.random() * nonTargetFiles.length)]);
      } else if (targetFile) {
        arr.push(targetFile);
      } else break;
    }
    if (arr.length > numberDesired) arr.length = numberDesired;
    return arr;
  };

  const fetchSelectedAudio = async () => {
    try {
      setTargetScore(0);
      setNonTargetScore(0);
      setTargetStimulusCount(0);
      setNonTargetStimulusCount(0);
      setDisplayScript('');
      setCurrentStimulusIndex(0);

      const candidates = buildCandidates(folderPath);
      if (candidates.length === 0) {
        setPopupError('Invalid folderPath (no candidates)');
        setShowPopup(true);
        return;
      }

      try { abortControllerRef.current.abort(); } catch (e) {}
      abortControllerRef.current = new AbortController();
      isPlayingRef.current = true;

      const tryResult = await tryListFilesWithCandidates(candidates);
      if (!tryResult) {
        setPopupError('Directory not found on server. Check folderPath.');
        setShowPopup(true);
        return;
      }

      const { files: audioData, usedPath } = tryResult;
      const validAudioFiles = audioData.filter(f => f && typeof f === 'string' && f.trim().toLowerCase().endsWith('.wav')).map(f => f.trim());
      if (validAudioFiles.length === 0) {
        setPopupError('No audio files found in directory.');
        setShowPopup(true);
        return;
      }

      let numTargetStimuli = Math.round((targetPercentage / 100) * numberOfStimuli);
      if (numTargetStimuli < 0) numTargetStimuli = 0;
      if (numTargetStimuli > numberOfStimuli) numTargetStimuli = numberOfStimuli;
      const numStandardStimuli = numberOfStimuli - numTargetStimuli;

      const normalizedTarget = (targetStimuli || '').trim().replace(/\s+/g, '').toLowerCase();
      const targetFile = validAudioFiles.find(file => file.replace(/\s+/g, '').replace('.wav', '').toLowerCase() === normalizedTarget);
      const nonTargetFiles = validAudioFiles.filter(file => (file.replace(/\s+/g, '').replace('.wav', '').toLowerCase()) !== normalizedTarget);

      if (numTargetStimuli > 0 && !targetFile) {
        setPopupError('Target file not found in folder.');
        setShowPopup(true);
        return;
      }
      if (numStandardStimuli > 0 && nonTargetFiles.length === 0) {
        setPopupError('Not enough non-target files in folder.');
        setShowPopup(true);
        return;
      }

      const targetList = Array(numTargetStimuli).fill(targetFile);
      const standardList = [];
      for (let i = 0; i < numStandardStimuli; i++) standardList.push(nonTargetFiles[i % nonTargetFiles.length]);
      let combined = [...targetList, ...standardList];

      for (let i = combined.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [combined[i], combined[j]] = [combined[j], combined[i]];
      }
      combined = ensureCombinedLength(combined, numberOfStimuli, nonTargetFiles, targetFile);
      stimulusSequenceRef.current = combined;

      // preload unique files & durations
      const unique = Array.from(new Set(combined));
      const preloadMap = {};
      for (const f of unique) {
        if (abortControllerRef.current.signal.aborted) throw new Error('Aborted');
        const info = await fetchAudioBlobAndDuration(`${usedPath}/${f}`);
        preloadMap[f] = info;
      }

      // compute durations
      let totalPlaybackDuration = 0;
      for (const f of combined) totalPlaybackDuration += (preloadMap[f]?.duration ?? 0);

      // allocate available gaps (simple equal distribution)
      const n = combined.length;
      let totalAvailableForGaps = duration - totalPlaybackDuration - finalGapSeconds;
      if (totalAvailableForGaps < 0) totalAvailableForGaps = 0;
      const gapsCount = Math.max(0, n - 1);
      const intervals = gapsCount === 0 ? [] : Array(gapsCount).fill(totalAvailableForGaps / gapsCount);

      // set test watchdog
      if (testTimeoutRef.current) clearTimeout(testTimeoutRef.current);
      testTimeoutRef.current = setTimeout(() => {
        try { abortControllerRef.current.abort(); } catch (e) {}
      }, Math.ceil((totalPlaybackDuration + totalAvailableForGaps + finalGapSeconds + 2) * 1000));

      // Play sequence
      await playStimuliWithPreloadedUrls(combined, preloadMap, intervals);
    } catch (err) {
      if (err && err.message === 'Aborted') {
        /* ignore */
      } else {
        setPopupError(String(err.message || err));
        setShowPopup(true);
      }
      setIsTestRunning(false);
    } finally {
      if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
    }
  };

  const playAudioFromUrl = (blobUrl, fileName) => {
    return new Promise((resolve, reject) => {
      if (abortControllerRef.current.signal.aborted) return reject(new Error('Aborted'));
      const audioEl = audioRef.current;
      audioEl.pause();
      audioEl.src = blobUrl;
      audioEl.currentTime = 0;

      const onEnded = () => { cleanup(); resolve(); };
      const onError = (e) => { cleanup(); reject(e || new Error('Audio playback error')); };
      const onAbort = () => { cleanup(); reject(new Error('Aborted')); };

      const cleanup = () => {
        audioEl.removeEventListener('ended', onEnded);
        audioEl.removeEventListener('error', onError);
        abortControllerRef.current.signal.removeEventListener('abort', onAbort);
      };

      audioEl.addEventListener('ended', onEnded);
      audioEl.addEventListener('error', onError);
      abortControllerRef.current.signal.addEventListener('abort', onAbort);

      const playPromise = audioEl.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(err => { cleanup(); reject(err); });
      }
    });
  };

  const playStimuliWithPreloadedUrls = async (fileList, preloadMap, intervals) => {
    try {
      for (let i = 0; i < fileList.length; i++) {
        if (abortControllerRef.current.signal.aborted) break;
        const file = fileList[i];
        setCurrentStimulusIndex(i);

        // show script DURING audio playback only
        setDisplayScript(file.replace('.wav', ''));

        // open response window when audio starts
        setIsTestRunning(true);
        setResponseWindow(Math.max(responseWindow, 0)); // ensure it's a number (keeps state)
        if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
        setTimeout(() => {}, 0); // no-op, keep event loop consistent
        setTimeout(() => {}, 0);

        // open response-window and schedule its close AFTER responseWindow seconds
        setTimeout(() => {}, 0);
        setTimeout(() => {}, 0);

        // Set response-window flag and timer (independent from displayScript)
        setTimeout(() => {}, 0); // minor no-op
        setTimeout(() => {}, 0);
        // actual response-window handling:
        setIsWithinResponseWindow(true);
        respTimeoutRef.current = setTimeout(() => {
          respTimeoutRef.current = null;
          setIsWithinResponseWindow(false);
        }, Math.round(responseWindow * 1000));

        // Play audio; await until end (or abort)
        try {
          await playAudioFromUrl(preloadMap[file].url, file);
        } catch (err) {
          if (err && err.message === 'Aborted') {
            // cleanup displayScript if aborted
            setDisplayScript('');
            throw err;
          } else {
            // ensure display hides even on error
            setDisplayScript('');
            console.error('Playback error for', file, err);
            throw err;
          }
        }

        // Immediately hide script when audio completes (even if response window still open)
        setDisplayScript('');

        // update stimulus counters
        const isTarget = file.includes(targetStimuli);
        if (isTarget) {
          setTargetStimulusCount(prev => prev + 1);
        } else {
          setNonTargetStimulusCount(prev => prev + 1);
        }

        // wait interval (if not last)
        if (i < fileList.length - 1) {
          const waitMs = Math.max(0, (intervals[i] || 0) * 1000);
          try {
            await delay(waitMs, abortControllerRef.current.signal);
          } catch (e) {
            break; // aborted during gap
          }
        }
      }

      // final gap after last stimulus
      if (!abortControllerRef.current.signal.aborted) {
        try { await delay(finalGapSeconds * 1000, abortControllerRef.current.signal); } catch (e) {}
      }
    } catch (err) {
      if (err && err.message !== 'Aborted') {
        console.error('playStimuliWithPreloadedUrls error', err);
      }
    } finally {
      // ensure response window timeout cleared and flags reset
      if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
      setIsWithinResponseWindow(false);
      setDisplayScript('');
      setIsTestRunning(false);
      setShowPopup(true);

      // revoke preloaded object URLs
      try {
        Object.values(preloadMap || {}).forEach(i => { try { URL.revokeObjectURL(i.url); } catch (e) {} });
      } catch (e) {}
      if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
    }
  };

  const handleClick = () => {
    if (!isTestRunning || !isWithinResponseWindow) return;
    const currentStimulus = stimulusSequenceRef.current[currentStimulusIndex];
    if (!currentStimulus) return;
    const isTarget = currentStimulus.includes(targetStimuli);
    if (isTarget) {
      setTargetScore(prev => prev + 1);
    } else {
      setNonTargetScore(prev => prev + 1);
    }
  };

  const handleExit = () => {
    try { abortControllerRef.current.abort(); } catch (e) {}
    try { audioRef.current.pause(); } catch (e) {}
    audioRef.current.src = '';
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
    if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
    setIsTestRunning(false);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setPopupError(null);
    navigate('/home');
  };

  useEffect(() => {
    // debug watch
  }, [targetScore, nonTargetScore, targetStimulusCount, nonTargetStimulusCount, currentStimulusIndex]);

  return (
    <div className="test-screen11">
      <Goal g={g} sk={sk} />
      <div className="content">
        <h4>{instruction}</h4>
        <div className="audio-icon" onClick={() => { try { audioRef.current.play(); } catch (e) {} }}>
          <span role="img" aria-label="speaker">🔊</span>
        </div>
        {/* displayScript now only shows during audio playback and is cleared when audio completes */}
        <h2 className="display-script">{displayScript}</h2>
      </div>

      <div className="button-row-index">
        <button className="click-button" onClick={handleClick} disabled={!isTestRunning}>Click</button>
        <button className="exit-button" onClick={handleExit}>Exit</button>
      </div>

      {showPopup && (
        <Popup
          targetScore={targetScore}
          targetStimulusCount={targetStimulusCount}
          nonTargetScore={nonTargetScore}
          nonTargetStimulusCount={nonTargetStimulusCount}
          onClose={handleClosePopup}
          isTestScreen12={true}
          errorMessage={popupError}
        />
      )}
    </div>
  );
};

export default TestScreen12;


// import React, { useContext, useEffect, useState, useRef } from 'react';
// import DataContext from '../../stores/DataContextProvider';
// import { useNavigate } from 'react-router-dom';
// import backendIP from '../../utils/serverData';
// import Popup from '../popup/Popup';
// import './TestScreen11.css';
// import Goal from '../controllers/Goal';

// const delay = (ms, signal) => {
//   console.log(`[delay] requested ${ms}ms`);
//   return new Promise((resolve, reject) => {
//     if (signal.aborted) {
//       console.log('[delay] aborted immediately');
//       return reject(new Error('Aborted'));
//     }
//     const timeout = setTimeout(() => {
//       console.log(`[delay] resolved after ${ms}ms`);
//       resolve();
//     }, ms);
//     signal.addEventListener('abort', () => {
//       clearTimeout(timeout);
//       console.log('[delay] aborted during wait');
//       reject(new Error('Aborted'));
//     });
//   });
// };

// const TestScreen12 = () => {
//   console.log('[TestScreen12] render start');
//   const { g, sk, instruction, selectedOptions, folderPath } = useContext(DataContext);
//   console.log('[TestScreen12] context values:', { g, sk, instruction, selectedOptions, folderPath });

//   const [displayScript, setDisplayScript] = useState('');
//   const [showPopup, setShowPopup] = useState(false);
//   const [popupError, setPopupError] = useState(null);
//   const audioRef = useRef(new Audio());
//   const abortControllerRef = useRef(new AbortController());
//   const navigate = useNavigate();

//   const [isTestRunning, setIsTestRunning] = useState(false);
//   const [numberOfStimuli, setNumberOfStimuli] = useState(0);
//   const [targetStimuli, setTargetStimuli] = useState('');
//   const [targetPercentage, setTargetPercentage] = useState(0);
//   const [duration, setDuration] = useState(''); // seconds

//   const [targetScore, setTargetScore] = useState(0);
//   const [nonTargetScore, setNonTargetScore] = useState(0);
//   const [targetStimulusCount, setTargetStimulusCount] = useState(0);
//   const [nonTargetStimulusCount, setNonTargetStimulusCount] = useState(0);

//   const [currentStimulusIndex, setCurrentStimulusIndex] = useState(0);
//   const timeoutRef = useRef(null);
//   const [responseWindow, setResponseWindow] = useState(3); // seconds

//   const stimulusSequenceRef = useRef([]);
//   const isPlayingRef = useRef(false);
//   const startTimeRef = useRef(null);
//   const testTimeoutRef = useRef(null);
//   const finalGapSeconds = 10; // final gap after last stimulus

//   // ---------- helpers ----------
//   const getOption = (keys = []) => {
//     if (!selectedOptions) return undefined;
//     for (const k of keys) {
//       if (Object.prototype.hasOwnProperty.call(selectedOptions, k)) return selectedOptions[k];
//     }
//     return undefined;
//   };

//   const parseDurationMinutes = (durationOption) => {
//     if (durationOption == null) return 5;
//     if (typeof durationOption === 'object') {
//       if (typeof durationOption.value === 'number') return durationOption.value;
//       if (typeof durationOption.value === 'string') {
//         const m = durationOption.value.match(/\d+/);
//         if (m) return parseInt(m[0], 10);
//       }
//       if (typeof durationOption.label === 'string') {
//         const m = durationOption.label.match(/\d+/);
//         if (m) return parseInt(m[0], 10);
//       }
//       for (const v of Object.values(durationOption)) {
//         if (typeof v === 'string') {
//           const m = v.match(/\d+/);
//           if (m) return parseInt(m[0], 10);
//         }
//         if (typeof v === 'number') return v;
//       }
//     }
//     if (typeof durationOption === 'string') {
//       const m = durationOption.match(/\d+/);
//       if (m) return parseInt(m[0], 10);
//     }
//     if (typeof durationOption === 'number') return durationOption;
//     return 5;
//   };

//   // Normalize provided raw path and generate candidate variants for backend listfiles
//   const normalizeRawPath = (raw) => {
//     if (!raw || typeof raw !== 'string') return null;
//     let s = raw.trim();
//     const original = s;

//     // Replace backslashes with forward slashes
//     const forward = s.replace(/\\/g, '/');

//     // collapse repeated slashes but keep leading UNC '//' if present
//     const leadingUNC = forward.startsWith('//') ? '//' : '';
//     let tmp = forward.replace(/^\/+/, '').replace(/\/+/g, '/');
//     if (leadingUNC) tmp = leadingUNC + tmp;

//     // dedupe adjacent duplicates
//     const parts = tmp.split('/').filter(Boolean);
//     const dedupedParts = parts.reduce((acc, p) => {
//       if (acc.length === 0 || acc[acc.length - 1] !== p) acc.push(p);
//       return acc;
//     }, []);
//     const deduped = (leadingUNC ? '//' : '') + dedupedParts.join('/');

//     return { original, forward, deduped, parts: dedupedParts, leadingUNC };
//   };

//   const buildCandidates = (rawPath) => {
//     const res = normalizeRawPath(rawPath);
//     if (!res) return [];
//     const out = [];

//     // raw, forward-normalized, deduped
//     out.push(res.original);
//     out.push(res.forward);
//     out.push(res.deduped);

//     // try without leading slashes
//     out.push(res.deduped.replace(/^\/+/, ''));

//     // try windows-style backslashes
//     out.push((res.leadingUNC ? '\\\\' : '') + res.parts.join('\\'));

//     // defensive: keep base up to the first 'cog*' token and only last segment (user folder)
//     const lowerParts = res.parts.map(p => p.toLowerCase());
//     const baseIndex = lowerParts.findIndex(p => /cog/i.test(p) || /cogniselsusatt/i.test(p) || /cogwordssus/i.test(p));
//     if (baseIndex >= 0) {
//       const base = res.parts.slice(0, baseIndex + 1);
//       const last = res.parts[res.parts.length - 1];
//       out.push((res.leadingUNC ? '//' : '') + [...base, last].join('/'));
//     }

//     // try only last 1 or 2 segments (sometimes backend expects relative)
//     if (res.parts.length >= 2) out.push(res.parts.slice(-2).join('/'));
//     out.push(res.parts.slice(-1).join('/'));

//     // unique preserve order
//     return Array.from(new Set(out.filter(Boolean)));
//   };

//   // Try listfiles with multiple folderPath candidates until one works. Returns {files, usedPath} or null
//   const tryListFilesWithCandidates = async (candidates) => {
//     let lastErr = null;
//     for (const cand of candidates) {
//       try {
//         console.log('[tryListFilesWithCandidates] trying candidate ->', cand);
//         const resp = await fetch(`${backendIP}/audio/listfiles`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify({ folderPath: cand }),
//           signal: abortControllerRef.current.signal,
//         });
//         console.log('[tryListFilesWithCandidates] response status:', resp.status, resp.statusText, 'for', cand);
//         if (!resp.ok) {
//           const text = await resp.text().catch(() => '');
//           console.warn('[tryListFilesWithCandidates] candidate failed:', cand, resp.status, text);
//           lastErr = { cand, status: resp.status, text };
//           continue;
//         }
//         const json = await resp.json();
//         console.log('[tryListFilesWithCandidates] success candidate:', cand, 'filesCount:', Array.isArray(json) ? json.length : 'NA');
//         return { files: json, usedPath: cand };
//       } catch (err) {
//         console.warn('[tryListFilesWithCandidates] fetch error for candidate', cand, err);
//         lastErr = err;
//         if (abortControllerRef.current.signal.aborted) break;
//       }
//     }
//     console.error('[tryListFilesWithCandidates] all candidates failed', lastErr);
//     return null;
//   };

//   // ---------- parsing selectedOptions ----------
//   useEffect(() => {
//     console.log('[useEffect:selectedOptions] fired', selectedOptions);
//     if (selectedOptions && Object.keys(selectedOptions).length > 0) {
//       const numStimOpt = getOption(['Number of Stimuli', 'NumberOfStimuli', 'Number']);
//       const numStimuliRaw = numStimOpt?.label ?? numStimOpt?.value ?? numStimOpt;
//       const numStimuli = parseInt(numStimuliRaw, 10) || 0;

//       const targetStimuliOpt = getOption(['Target Stimuli', 'TargetStimuli', 'Target']);
//       const targetStimuliLabel = (targetStimuliOpt?.label ?? targetStimuliOpt?.value ?? targetStimuliOpt ?? '').toString();

//       const targetPercentageOpt = getOption(['Target Percentage', 'TargetPercentage', 'Target %']);
//       const targetPercentageValue = targetPercentageOpt != null ? parseInt(targetPercentageOpt, 10) : 0;

//       const durationOption = getOption(['Duration', 'Time Duration', 'TimeDuration', 'Time']);
//       const durationMinutes = parseDurationMinutes(durationOption);
//       const durationSeconds = durationMinutes * 60;

//       const responseWindowOpt = getOption(['Response Window', 'ResponseWindow', 'Response']);
//       let responseWindowSeconds = 3;
//       if (responseWindowOpt != null) {
//         if (typeof responseWindowOpt === 'number') {
//           responseWindowSeconds = responseWindowOpt > 1000 ? responseWindowOpt / 1000 : responseWindowOpt;
//         } else if (typeof responseWindowOpt === 'string') {
//           const m = responseWindowOpt.match(/\d+/);
//           if (m) {
//             const val = parseInt(m[0], 10);
//             responseWindowSeconds = val > 1000 ? val / 1000 : val;
//           }
//         } else if (typeof responseWindowOpt === 'object') {
//           const raw = responseWindowOpt.value ?? responseWindowOpt.label ?? responseWindowOpt;
//           if (typeof raw === 'number') responseWindowSeconds = raw > 1000 ? raw / 1000 : raw;
//           else if (typeof raw === 'string') {
//             const m = raw.match(/\d+/);
//             if (m) {
//               const val = parseInt(m[0], 10);
//               responseWindowSeconds = val > 1000 ? val / 1000 : val;
//             }
//           }
//         }
//       }

//       console.log('[useEffect:selectedOptions] parsed values:', {
//         numStimuli,
//         targetStimuliLabel,
//         targetPercentageValue,
//         durationMinutes,
//         durationSeconds,
//         responseWindowSeconds,
//       });

//       if (!isNaN(numStimuli) && numStimuli > 0) {
//         // reset old run
//         stimulusSequenceRef.current = [];
//         if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
//         if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
//         try { abortControllerRef.current.abort(); } catch (e) {}
//         abortControllerRef.current = new AbortController();

//         setNumberOfStimuli(numStimuli);
//         setTargetStimuli(targetStimuliLabel);
//         setTargetPercentage(targetPercentageValue);
//         setDuration(durationSeconds);
//         setResponseWindow(responseWindowSeconds);

//         setIsTestRunning(true); // will trigger fetchSelectedAudio effect
//         setPopupError(null);
//         console.log('[useEffect:selectedOptions] test started');
//       } else {
//         console.error('Invalid Number of Stimuli:', numStimuli);
//       }
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedOptions]);

//   // don't set global timeout here; will set after preload
//   useEffect(() => {
//     return () => {
//       if (testTimeoutRef.current) {
//         clearTimeout(testTimeoutRef.current);
//         testTimeoutRef.current = null;
//       }
//     };
//   }, [isTestRunning, duration]);

//   // when folderPath/numberOfStimuli/duration/isTestRunning change -> fetch
//   useEffect(() => {
//     console.log('[useEffect] deps changed:', { folderPath, numberOfStimuli, targetStimuli, targetPercentage, duration, isTestRunning });
//     if (folderPath && numberOfStimuli > 0 && duration > 0 && isTestRunning) {
//       fetchSelectedAudio();
//     }
//     return () => {
//       try { abortControllerRef.current.abort(); } catch (e) {}
//       audioRef.current.pause();
//       audioRef.current.src = '';
//       if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
//       if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [folderPath, numberOfStimuli, targetStimuli, targetPercentage, duration, isTestRunning]);

//   // fetch audio list & play
//   const fetchAudioBlobAndDuration = async (filenameWithPath) => {
//     console.log('[fetchAudioBlobAndDuration] fetching', filenameWithPath);
//     const resp = await fetch(`${backendIP}/audio/getaudio`, {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ filenameWithPath }),
//       signal: abortControllerRef.current.signal,
//     });
//     if (!resp.ok) {
//       const errText = await resp.text().catch(()=>'');
//       throw new Error(`[getaudio] ${resp.status} ${resp.statusText} - ${errText}`);
//     }
//     const blob = await resp.blob();
//     const url = URL.createObjectURL(blob);

//     // get duration via temporary audio element
//     const tempAudio = new Audio();
//     tempAudio.src = url;
//     await new Promise((resolve, reject) => {
//       const onLoaded = () => { cleanup(); resolve(); };
//       const onError = (e) => { cleanup(); reject(new Error('loadedmetadata failed')); };
//       const cleanup = () => {
//         tempAudio.removeEventListener('loadedmetadata', onLoaded);
//         tempAudio.removeEventListener('error', onError);
//       };
//       tempAudio.addEventListener('loadedmetadata', onLoaded);
//       tempAudio.addEventListener('error', onError);
//     });
//     const dur = tempAudio.duration || 0;
//     console.log('[fetchAudioBlobAndDuration] got duration', dur, 'for', filenameWithPath);
//     return { url, duration: dur };
//   };

//   const ensureCombinedLength = (combinedFiles, numberDesired, nonTargetFiles, targetFile) => {
//     // make a shallow copy
//     const arr = combinedFiles.slice();
//     // if short, fill from nonTargetFiles (or targetFile) randomly until reaches desired
//     while (arr.length < numberDesired) {
//       if (nonTargetFiles && nonTargetFiles.length > 0) {
//         const pick = nonTargetFiles[Math.floor(Math.random() * nonTargetFiles.length)];
//         arr.push(pick);
//       } else if (targetFile) {
//         arr.push(targetFile);
//       } else {
//         break;
//       }
//     }
//     // if longer, truncate to exact numberDesired
//     if (arr.length > numberDesired) arr.length = numberDesired;
//     return arr;
//   };

//   const fetchSelectedAudio = async () => {
//     console.log('[fetchSelectedAudio] start', { folderPath, numberOfStimuli, targetStimuli, targetPercentage, duration });
//     try {
//       setTargetScore(0);
//       setNonTargetScore(0);
//       setTargetStimulusCount(0);
//       setNonTargetStimulusCount(0);
//       setDisplayScript('');
//       setCurrentStimulusIndex(0);

//       // build candidate paths and try listfiles
//       const candidates = buildCandidates(folderPath);
//       console.log('[fetchSelectedAudio] built candidates:', candidates);
//       if (candidates.length === 0) {
//         console.error('[fetchSelectedAudio] no folderPath candidates');
//         setPopupError('Invalid folderPath (no candidates)');
//         setShowPopup(true);
//         return;
//       }

//       try { abortControllerRef.current.abort(); } catch (e) {}
//       abortControllerRef.current = new AbortController();
//       isPlayingRef.current = true;

//       const preloadStart = Date.now();

//       const tryResult = await tryListFilesWithCandidates(candidates);
//       if (!tryResult) {
//         console.error('[fetchSelectedAudio] directory not found (404) - candidates tried. See log');
//         setPopupError('Directory not found on server. Check folderPath.');
//         setShowPopup(true);
//         return;
//       }

//       const { files: audioData, usedPath } = tryResult;
//       console.log('[fetchSelectedAudio] using path:', usedPath);

//       const validAudioFiles = audioData.filter(file => file && file.trim().endsWith('.wav')).map(file => file.trim());
//       console.log('[fetchSelectedAudio] validAudioFiles:', validAudioFiles);

//       const normalizedTargetStimulus = (targetStimuli || '').trim().replace(/\s+/g, '').toLowerCase();
//       console.log('[fetchSelectedAudio] normalizedTargetStimulus:', normalizedTargetStimulus);

//       // compute counts (ensure sum equals numberOfStimuli)
//       let numTargetStimuli = Math.round((targetPercentage / 100) * numberOfStimuli);
//       if (numTargetStimuli < 0) numTargetStimuli = 0;
//       if (numTargetStimuli > numberOfStimuli) numTargetStimuli = numberOfStimuli;
//       let numStandardStimuli = numberOfStimuli - numTargetStimuli; // ensures sum equals numberOfStimuli
//       console.log('[fetchSelectedAudio] target/standard counts:', { numTargetStimuli, numStandardStimuli });

//       if (validAudioFiles.length === 0) {
//         console.error('[fetchSelectedAudio] no .wav files returned by server');
//         setPopupError('No audio files found in directory.');
//         setShowPopup(true);
//         return;
//       }

//       const targetFile = validAudioFiles.find(file => {
//         const normalizedFileName = file.replace(/\s+/g, '').replace('.wav', '').toLowerCase();
//         return normalizedFileName === normalizedTargetStimulus;
//       });

//       const nonTargetFiles = validAudioFiles.filter(file => {
//         const normalizedFileName = file.replace(/\s+/g, '').replace('.wav', '').toLowerCase();
//         return normalizedFileName !== normalizedTargetStimulus;
//       });

//       console.log('[fetchSelectedAudio] targetFile:', targetFile, 'nonTargetFiles count:', nonTargetFiles.length);

//       if (numTargetStimuli > 0 && !targetFile) {
//         console.error('Target stimulus file not found but requested count > 0');
//         setPopupError('Target file not found in folder.');
//         setShowPopup(true);
//         return;
//       }

//       if (numStandardStimuli > 0 && nonTargetFiles.length === 0) {
//         console.error('No standard stimuli files found!');
//         setPopupError('Not enough non-target files in folder.');
//         setShowPopup(true);
//         return;
//       }

//       // build arrays
//       const targetStimuliFiles = Array(numTargetStimuli).fill(targetFile);
//       const standardStimuliFiles = [];
//       for (let i = 0; i < numStandardStimuli; i++) {
//         // cycle over nonTargetFiles if necessary
//         standardStimuliFiles.push(nonTargetFiles[i % nonTargetFiles.length]);
//       }

//       let combinedFiles = [...targetStimuliFiles, ...standardStimuliFiles];

//       // shuffle
//       for (let i = combinedFiles.length - 1; i > 0; i--) {
//         const j = Math.floor(Math.random() * (i + 1));
//         [combinedFiles[i], combinedFiles[j]] = [combinedFiles[j], combinedFiles[i]];
//       }

//       // Ensure exact numberOfStimuli (fix off-by-one / missing stimuli)
//       combinedFiles = ensureCombinedLength(combinedFiles, numberOfStimuli, nonTargetFiles, targetFile);
//       console.log('[fetchSelectedAudio] combinedFiles (final) length:', combinedFiles.length, combinedFiles);

//       stimulusSequenceRef.current = combinedFiles;

//       // Preload unique files sequentially and get durations
//       const uniqueFiles = Array.from(new Set(combinedFiles));
//       console.log('[fetchSelectedAudio] uniqueFiles to preload:', uniqueFiles);

//       const preloadMap = {};
//       try {
//         for (const file of uniqueFiles) {
//           if (abortControllerRef.current.signal.aborted) throw new Error('Aborted');
//           const filenameWithPath = `${usedPath}/${file}`;
//           const info = await fetchAudioBlobAndDuration(filenameWithPath);
//           preloadMap[file] = info;
//         }
//       } catch (err) {
//         console.error('[fetchSelectedAudio] error preloading', err);
//         throw err;
//       }

//       const preloadElapsed = (Date.now() - preloadStart) / 1000;
//       console.log('[fetchSelectedAudio] preloadElapsed (s):', preloadElapsed);

//       // compute durations of sequence and allocate gaps (accounting preload elapsed time)
//       let totalPlaybackDuration = 0;
//       for (const f of combinedFiles) totalPlaybackDuration += (preloadMap[f]?.duration ?? 0);
//       console.log('[fetchSelectedAudio] totalPlaybackDuration (s):', totalPlaybackDuration);

//       // Reserve finalGapSeconds for final gap — subtract from gaps pool
//       let totalAvailableForGaps = duration - totalPlaybackDuration - preloadElapsed - finalGapSeconds;
//       console.log('[fetchSelectedAudio] totalAvailableForGaps after preload (s) (finalGap reserved):', totalAvailableForGaps);

//       const n = combinedFiles.length;
//       let intervals = [];
//       if (n <= 1) intervals = [];
//       else {
//         if (totalAvailableForGaps <= 0) {
//           const tinyGap = 0.5;
//           intervals = Array(n - 1).fill(tinyGap);
//           // set test timeout to worst-case (playback + tiny gaps + finalGap)
//           const worstCaseTime = totalPlaybackDuration + tinyGap * (n - 1) + finalGapSeconds;
//           if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
//           testTimeoutRef.current = setTimeout(() => {
//             console.log('[testTimeout] worstCaseTime elapsed, calling handleExit');
//             handleExit();
//           }, Math.ceil(worstCaseTime * 1000) + 500);
//         } else {
//           const gapPerInterval = totalAvailableForGaps / (n - 1);
//           intervals = Array(n - 1).fill(gapPerInterval);
//           if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
//           const remainingTotalMs = Math.ceil((totalPlaybackDuration + totalAvailableForGaps + finalGapSeconds) * 1000) + 500;
//           testTimeoutRef.current = setTimeout(() => {
//             console.log('[testTimeout] (post-preload) duration elapsed, calling handleExit');
//             handleExit();
//           }, remainingTotalMs);
//         }
//       }

//       // Play using preloaded urls -- IMPORTANT: await so we wait until playback+final-gap finishes
//       await playStimuliWithPreloadedUrls(combinedFiles, preloadMap, intervals);
//       // now playback + final gap finished; testTimeout will be cleared in finally of playStimuliWithPreloadedUrls

//     } catch (error) {
//       if (error && error.message === 'Aborted') {
//         console.log('[fetchSelectedAudio] aborted');
//       } else {
//         console.error('Error in fetchSelectedAudio:', error);
//         if (!popupError) {
//           setPopupError(String(error.message || error));
//           setShowPopup(true);
//         }
//       }
//     }
//   };

//   const playAudioFromUrl = (blobUrl, fileName) => {
//     console.log('[playAudioFromUrl] playing', fileName, blobUrl);
//     return new Promise((resolve, reject) => {
//       if (abortControllerRef.current.signal.aborted) return reject(new Error('Aborted'));
//       const audioEl = audioRef.current;
//       audioEl.pause();
//       audioEl.src = blobUrl;
//       audioEl.currentTime = 0;

//       const onEnded = () => { cleanup(); resolve(); };
//       const onError = (e) => { cleanup(); reject(e || new Error('Audio playback error')); };
//       const onAbort = () => { cleanup(); reject(new Error('Aborted')); };

//       const cleanup = () => {
//         audioEl.removeEventListener('ended', onEnded);
//         audioEl.removeEventListener('error', onError);
//         abortControllerRef.current.signal.removeEventListener('abort', onAbort);
//       };

//       audioEl.addEventListener('ended', onEnded);
//       audioEl.addEventListener('error', onError);
//       abortControllerRef.current.signal.addEventListener('abort', onAbort);

//       const playPromise = audioEl.play();
//       if (playPromise && typeof playPromise.then === 'function') {
//         playPromise.catch(err => { cleanup(); reject(err); });
//       }
//     });
//   };

//   const playStimuliWithPreloadedUrls = async (fileList, preloadMap, intervals) => {
//     console.log('[playStimuliWithPreloadedUrls] start', { fileListLength: fileList.length, intervals });
//     try {
//       for (let i = 0; i < fileList.length; i++) {
//         if (abortControllerRef.current.signal.aborted) {
//           console.log('[playStimuliWithPreloadedUrls] abort detected, breaking');
//           break;
//         }
//         const file = fileList[i];
//         setCurrentStimulusIndex(i);
//         setDisplayScript(file.replace('.wav', ''));
//         console.log('[playStimuliWithPreloadedUrls] playing index', i, file);

//         const isTarget = file.includes(targetStimuli);
//         if (isTarget) {
//           setTargetStimulusCount(prev => {
//             const next = prev + 1;
//             console.log('[play] increment targetStimulusCount', prev, '->', next);
//             return next;
//           });
//         } else {
//           setNonTargetStimulusCount(prev => {
//             const next = prev + 1;
//             console.log('[play] increment nonTargetStimulusCount', prev, '->', next);
//             return next;
//           });
//         }

//         const info = preloadMap[file];
//         if (!info) throw new Error('preload info missing for ' + file);

//         await playAudioFromUrl(info.url, file);

//         if (timeoutRef.current) clearTimeout(timeoutRef.current);
//         timeoutRef.current = setTimeout(() => {
//           console.log('[playStimuliWithPreloadedUrls] clearing displayScript after responseWindow');
//           setDisplayScript('');
//         }, responseWindow * 1000);

//         if (i < fileList.length - 1) {
//           const waitMs = Math.max(0, (intervals[i] || 0) * 1000);
//           console.log(`[playStimuliWithPreloadedUrls] waiting ${waitMs}ms before next stimulus`);
//           await delay(waitMs, abortControllerRef.current.signal);
//         }
//       }

//       // final gap AFTER last stimulus — awaited here so caller knows playback fully finished
//       if (!abortControllerRef.current.signal.aborted) {
//         console.log(`[playStimuliWithPreloadedUrls] final gap ${finalGapSeconds}s`);
//         try { await delay(finalGapSeconds * 1000, abortControllerRef.current.signal); } catch (e) { /* aborted */ }
//       }
//     } catch (err) {
//       if (err.message === 'Aborted') console.log('[playStimuliWithPreloadedUrls] aborted');
//       else console.error('[playStimuliWithPreloadedUrls] playback error', err);
//     } finally {
//       console.log('[playStimuliWithPreloadedUrls] finished playback loop');
//       setIsTestRunning(false);
//       isPlayingRef.current = false;
//       setShowPopup(true);

//       if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }

//       // revoke blob URLs
//       try {
//         const uniqueUrls = new Set(Object.values(preloadMap || {}).map(i => i.url));
//         uniqueUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
//       } catch (e) { console.warn('[playStimuliWithPreloadedUrls] revoke error', e); }
//     }
//   };

//   const handleClick = () => {
//     console.log('[handleClick] clicked', { isTestRunning, displayScript, currentStimulusIndex });
//     if (!isTestRunning || displayScript === '') { console.log('[handleClick] ignored'); return; }
//     const currentStimulus = stimulusSequenceRef.current[currentStimulusIndex];
//     if (currentStimulus) {
//       const isTarget = currentStimulus.includes(targetStimuli);
//       if (isTarget) {
//         setTargetScore(prev => {
//           const next = prev + 1;
//           console.log('[handleClick] increment targetScore', prev, '->', next);
//           return next;
//         });
//       } else {
//         setNonTargetScore(prev => {
//           const next = prev + 1;
//           console.log('[handleClick] increment nonTargetScore', prev, '->', next);
//           return next;
//         });
//       }
//     } else {
//       console.warn('[handleClick] no currentStimulus at index', currentStimulusIndex);
//     }
//   };

//   const handleExit = () => {
//     console.log('[handleExit] cleaning up');
//     try { abortControllerRef.current.abort(); } catch (e) {}
//     audioRef.current.pause();
//     audioRef.current.src = '';
//     if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
//     if (testTimeoutRef.current) { clearTimeout(testTimeoutRef.current); testTimeoutRef.current = null; }
//     setIsTestRunning(false);
//     setShowPopup(true);
//   };

//   const handleClosePopup = () => {
//     setShowPopup(false);
//     setPopupError(null);
//     navigate('/home');
//   };

//   useEffect(() => {
//     console.log('[state watch]', { targetScore, nonTargetScore, targetStimulusCount, nonTargetStimulusCount, currentStimulusIndex });
//   }, [targetScore, nonTargetScore, targetStimulusCount, nonTargetStimulusCount, currentStimulusIndex]);

//   return (
//     <div className="test-screen11">
//       <Goal g={g} sk={sk} />
//       <div className="content">
//         <h4>{instruction}</h4>
//         <div className="audio-icon" onClick={() => { try { audioRef.current.play(); } catch(e) { console.warn(e); } }}>
//           <span role="img" aria-label="speaker">🔊</span>
//         </div>
//         <h2 className="display-script">{displayScript}</h2>
//       </div>

//       <div className="button-row-index">
//         <button className="click-button" onClick={handleClick} disabled={!isTestRunning}>Click</button>
//         <button className="exit-button" onClick={handleExit}>Exit</button>
//       </div>

//       {showPopup && (
//         <Popup
//           targetScore={targetScore}
//           targetStimulusCount={targetStimulusCount}
//           nonTargetScore={nonTargetScore}
//           nonTargetStimulusCount={nonTargetStimulusCount}
//           onClose={handleClosePopup}
//           isTestScreen12={true}
//           errorMessage={popupError}
//         />
//       )}
//     </div>
//   );
// };

// export default TestScreen12;
